import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  bootstrap,
  getDecoded,
  getJwt,
  getRefreshToken,
  isExpired,
  onChange,
  refresh,
} from '@/lib/auth';
import {
  getPouchDB,
  releaseLocal,
  setLocalReplacedHandler,
  setSyncProgressHandler,
  startSync,
  type SyncHandle,
} from '@/lib/couch';
import { adoptLegacyLocal } from '@/lib/legacy-local';
import { initStoreFromPouchDB } from '@/lib/init-store';
import { buildSafePartial } from '@/lib/store-merge';
import { isWriteUnsettled } from '@/lib/sync-queue';
import { docIdPart } from '@/lib/store-adapter';
import { useT } from '@/lib/i18n';
import { useApp } from '@/store';
import { FirstSyncScreen } from '@/components/FirstSyncScreen';

// Doc types whose ids can appear in the edit-authoritative store slices. An
// entity is "busy" when a shadow-write under any of these keys is unsettled.
const ENTITY_TYPES = ['customer', 'visit', 'inspection'];

// A tenant with more than this many remote docs on a cold login is treated as
// a "large" first sync worth showing the full-screen progress for. Tiny tenants
// finish instantly and never see the screen.
const COLD_SYNC_THRESHOLD = 100;
// If the remote doc_count never resolves within this window (offline / no
// count), stop waiting and drop the user into the app.
const SYNC_TARGET_TIMEOUT_MS = 8000;

interface Props {
  children: React.ReactNode;
}

enum Phase {
  LOADING = 'loading',
  AUTHED = 'authed',
  UNAUTHED = 'unauthed',
}

// Which login + tenant the session belongs to. A refresh can hand out a token
// for another tenant without leaving the AUTHED phase (impersonation, or an
// account moved to another tenant), and sync must follow it.
function ownerKeyOf(): string {
  const d = getDecoded();
  return d ? `${d.sub ?? ''}|${d.tenant_id ?? ''}` : '';
}

export function AuthGate({ children }: Props) {
  const location = useLocation();
  const t = useT();
  const [phase, setPhase] = useState<Phase>(Phase.LOADING);
  const [ownerKey, setOwnerKey] = useState<string>(ownerKeyOf);
  // Bumped when sync deletes and recreates the local replica (a technician's
  // customers changed, client #92), so the store re-reads it from scratch.
  const [replicaEpoch, setReplicaEpoch] = useState(0);
  const syncRef = useRef<SyncHandle | null>(null);
  // The pre-split shared replica is carried over once per page load, for
  // whichever login is signed in — resumed or typed in, since an update can
  // sign a user out (legacy-local keeps it to that login's own tenants).
  const legacyAdoptedRef = useRef(false);
  const prevSubRef = useRef<string | null>(null);
  const prevTenantRef = useRef<string | null>(null);
  const initFiredRef = useRef<string | null>(null);
  const changesRef = useRef<{ cancel: () => void } | null>(null);
  const rehydrateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Whether THIS login is a cold first sync (store started empty + a large
  // remote). Warm logins (store already had customers) set this false and never
  // show the FirstSyncScreen.
  const [cold, setCold] = useState(false);
  const targetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstSyncComplete = useApp(s => s.firstSyncComplete);
  const syncTarget = useApp(s => s.syncTarget);

  useEffect(() => {
    setLocalReplacedHandler(() => {
      useApp.getState().resetDomain();
      setReplicaEpoch(e => e + 1);
    });
    return () => setLocalReplacedHandler(null);
  }, []);

  // Wire the couch progress handler into the store. Registered once, like the
  // store does for the online handler. Best-effort — never throws.
  useEffect(() => {
    setSyncProgressHandler((loaded, target) => {
      const st = useApp.getState();
      st.setSyncLoaded(loaded);
      if (target != null) st.setSyncTarget(target);
      // Complete when we've pulled at least as many docs as the tenant target.
      // Use >= (not ==) because audit docs live in a separate DB, so the
      // data_<tenant> target can equal or slightly undercount pulled docs.
      const tgt = target ?? st.syncTarget;
      if (tgt != null && loaded >= tgt && loaded > 0) {
        st.setFirstSyncComplete(true);
      }
    });
    return () => setSyncProgressHandler(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const jwt = getJwt();
      const decoded = getDecoded();
      if (jwt && decoded && !isExpired(decoded)) {
        if (!cancelled) setPhase(Phase.AUTHED);
        return;
      }
      const decodedAfter = await bootstrap();
      if (cancelled) return;
      setOwnerKey(ownerKeyOf());
      setPhase(decodedAfter ? Phase.AUTHED : Phase.UNAUTHED);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const off = onChange(() => {
      setOwnerKey(ownerKeyOf());
      const decoded = getDecoded();
      if (!decoded) {
        setPhase(Phase.UNAUTHED);
        return;
      }
      if (isExpired(decoded)) {
        // Sign out only when the session was rejected (tokens cleared); a
        // server that is briefly away is retried on the next call.
        refresh().catch(async () => {
          if (!(await getRefreshToken())) setPhase(Phase.UNAUTHED);
        });
        return;
      }
      setPhase(Phase.AUTHED);
    });
    return off;
  }, []);

  useEffect(() => {
    if (phase !== Phase.AUTHED) {
      // Re-arm the loading state so the next login shows skeletons until the
      // store re-hydrates from PouchDB, and allow the one-shot init to re-fire.
      useApp.getState().setHydrated(false);
      initFiredRef.current = null;
      if (syncRef.current) {
        syncRef.current.stop();
        syncRef.current = null;
      }
      // Signed out or session lost. Each login has its own local replica, so
      // nothing is wiped here: an explicit logout deletes it (auth.logout), and
      // an expired session keeps its unsent edits for when that login returns.
      releaseLocal();
      prevSubRef.current = getDecoded()?.sub ?? null;
      return;
    }
    const decoded = getDecoded();
    const prevSub = prevSubRef.current;
    const prevTenant = prevTenantRef.current;
    const curTenant = decoded?.tenant_id ?? null;
    if (
      (prevSub && decoded && decoded.sub !== prevSub) ||
      (prevTenant && curTenant && prevTenant !== curTenant)
    ) {
      // Another login or tenant (impersonation, account moved): switch to that
      // owner's replica and remote. The previous one stays on disk untouched.
      if (syncRef.current) {
        syncRef.current.stop();
        syncRef.current = null;
      }
      releaseLocal();
      useApp.getState().resetDomain();
    }
    prevSubRef.current = decoded?.sub ?? null;
    prevTenantRef.current = curTenant;
    if (!syncRef.current) {
      syncRef.current = startSync();
    }
    if (!legacyAdoptedRef.current) {
      legacyAdoptedRef.current = true;
      void adoptLegacyLocal();
    }
    // One-shot hydrate Zustand from local PouchDB after sync starts (or
    // restart after tenant switch). PouchDB returns whatever has replicated
    // so far — empty on first run leaves the seed/persisted state intact.
    const initKey = `${decoded?.sub ?? ''}|${curTenant ?? ''}|${replicaEpoch}`;
    if (initKey && initFiredRef.current !== initKey) {
      initFiredRef.current = initKey;
      // Cold vs warm: capture whether the store already had customer data
      // BEFORE the one-shot hydrate. Warm login (data present) → mark the first
      // sync complete immediately and never show the screen. Cold login (empty)
      // → wait for the remote target; if it comes back large, flip `cold` on so
      // the FirstSyncScreen renders until replication catches up.
      const hadData = useApp.getState().customers.length > 0;
      const store = useApp.getState();
      store.setFirstSyncComplete(false);
      store.setSyncTarget(null);
      store.setSyncLoaded(0);
      setCold(false);
      if (targetTimeoutRef.current) clearTimeout(targetTimeoutRef.current);
      if (hadData) {
        store.setFirstSyncComplete(true);
      } else {
        // Poll for the target the progress handler will fill in. Once known and
        // large, treat this as a cold first sync; if it never resolves, fall
        // through to the app after the timeout so the user is never trapped.
        const started = Date.now();
        const checkTarget = (): void => {
          const s = useApp.getState();
          if (s.firstSyncComplete) return;
          if (s.syncTarget != null) {
            if (s.syncTarget > COLD_SYNC_THRESHOLD) {
              setCold(true);
            } else {
              // Small tenant — no screen; finishes via normal hydration.
              s.setFirstSyncComplete(true);
            }
            return;
          }
          if (Date.now() - started >= SYNC_TARGET_TIMEOUT_MS) {
            s.setFirstSyncComplete(true);
            return;
          }
          targetTimeoutRef.current = setTimeout(checkTarget, 300);
        };
        checkTarget();
      }
      void initStoreFromPouchDB().then(partial => {
        if (Object.keys(partial).length) {
          useApp.setState(partial as never);
        }
        // First hydrate attempt resolved — drop the loading skeletons even if
        // empty (fresh/empty tenant), live updates keep filling the store.
        useApp.getState().setHydrated(true);
      });
      // Subscribe to local PouchDB changes so docs that arrive AFTER the
      // initial hydration (typical on first login when replication is still
      // catching up) get pulled into the store. Debounced + best-effort —
      // failures (e.g. local DB destroyed during teardown) are swallowed.
      // Use `since: 0` (not 'now') so docs that already landed during the
      // race between startSync and this listener attach also fire a change
      // and trigger a rehydrate.
      if (changesRef.current) {
        try {
          changesRef.current.cancel();
        } catch {
          /* ignore */
        }
        changesRef.current = null;
      }
      try {
        const local = getPouchDB();
        const feed = local
          .changes({ since: 0, live: true, include_docs: false })
          .on('change', () => {
            if (rehydrateTimerRef.current)
              clearTimeout(rehydrateTimerRef.current);
            rehydrateTimerRef.current = setTimeout(() => {
              void initStoreFromPouchDB().then(partial => {
                if (!Object.keys(partial).length) return;
                // Merge per entity, not per slice: an edit made on another
                // device has to show up here without a reload, while a local
                // edit whose shadow-put is still in flight must not be reverted
                // by the older snapshot. See lib/store-merge.
                const st = useApp.getState();
                const safe = buildSafePartial(st, partial, id =>
                  ENTITY_TYPES.some(type =>
                    isWriteUnsettled(`put:${type}:${docIdPart(type, id)}`)
                  )
                );
                if (Object.keys(safe).length) {
                  useApp.setState(safe as never);
                }
              });
            }, 250);
          })
          .on('error', () => {
            // Replication / DB tear-down may close the feed; ignore.
          });
        changesRef.current = { cancel: () => feed.cancel() };
      } catch {
        /* ignore — local DB may be unavailable */
      }
    }
    return () => {
      // keep sync alive across re-renders; teardown happens on unauth or unmount
    };
  }, [phase, ownerKey, replicaEpoch]);

  useEffect(() => {
    return () => {
      if (syncRef.current) {
        syncRef.current.stop();
        syncRef.current = null;
      }
      if (changesRef.current) {
        try {
          changesRef.current.cancel();
        } catch {
          /* ignore */
        }
        changesRef.current = null;
      }
      if (rehydrateTimerRef.current) {
        clearTimeout(rehydrateTimerRef.current);
        rehydrateTimerRef.current = null;
      }
      if (targetTimeoutRef.current) {
        clearTimeout(targetTimeoutRef.current);
        targetTimeoutRef.current = null;
      }
    };
  }, []);

  if (phase === Phase.LOADING) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        {t('auth.loading')}
      </div>
    );
  }
  if (phase === Phase.UNAUTHED) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // Cold first sync on a large tenant: hold the full-screen progress until the
  // initial replication finishes. Falls through once complete (or if the target
  // never resolves within the timeout, which flips firstSyncComplete=true).
  if (cold && !firstSyncComplete && syncTarget != null) {
    return <FirstSyncScreen />;
  }
  return <>{children}</>;
}
